import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

import type { UserAllocationCombinationsResponse } from "../../../types/userAllocations";
import {
  getUserAllocationCombinations,
  type DealsActiveFilter,
} from "../../userAllocations/userAllocationsApi";
import {
  normalizeUserAllocationCombinationsResponse,
  type CombinationSelection,
} from "./combinationUtils";
import { TeamPairingsByStatusTable } from "./TeamPairingsByStatusTable";

type UserAllocationCombinationsSectionProps = {
  dealsActive: DealsActiveFilter;
  fetchStatusIds?: number[];
  onSelectCombination: (selection: CombinationSelection) => void;
  onFetchError: (message: string) => void;
};

export function UserAllocationCombinationsSection({
  dealsActive,
  fetchStatusIds,
  onSelectCombination,
  onFetchError,
}: UserAllocationCombinationsSectionProps) {
  const [data, setData] = useState<UserAllocationCombinationsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getUserAllocationCombinations({
      dealsActive,
      statusIds: fetchStatusIds,
    })
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setData(normalizeUserAllocationCombinationsResponse(response.data));
          return;
        }
        onFetchError("Failed to load team pairings.");
        setData(null);
        setLoadError("Failed to load team pairings.");
      })
      .catch(() => {
        if (cancelled) return;
        onFetchError("Failed to fetch team pairings. Please try again.");
        setData(null);
        setLoadError("Failed to load team pairings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealsActive, fetchStatusIds]);

  const allPairs = useMemo(
    () =>
      (data?.pairs ?? [])
        .filter((p) => p.deal_count > 0)
        .slice()
        .sort((a, b) => b.deal_count - a.deal_count),
    [data?.pairs],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        mt: 1,
      }}
    >
      <Typography className="text-general-section-title">
        Team pairings
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : loadError ? (
        <Box sx={{ py: 2, color: "text.secondary" }} className="text-general-page-text">
          {loadError}
        </Box>
      ) : !data || allPairs.length === 0 ? (
        <Box sx={{ py: 2, color: "text.secondary" }} className="text-general-page-text">
          No team pairings match the current filters.
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            width: { xs: "100%", md: "50%" },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            background:
              "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.5, overflow: "auto" }}>
            <TeamPairingsByStatusTable
              pairs={allPairs}
              onSelectCombination={onSelectCombination}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}
